import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import { useOnScreen } from '@/hooks/useOnScreen';
import useDiscoveryFeed from '@/hooks/useDiscoveryFeed';
import useDiscoveryCatalog from '@/hooks/useDiscoveryCatalog';
import DiscoveryFeatureSection from './DiscoveryFeatureSection';
import { getCatalogPalette } from '@/components/Catalog/catalogPalette';
import IdeaGroup from './IdeaGroup';
import LocalBriefing from './LocalBriefing';
import RecentDiscovery from './RecentDiscovery';
import useDiscoveryExperience from '@/hooks/useDiscoveryExperience';

const HomeDiscoveryFeed = forwardRef(function HomeDiscoveryFeed({ enabled, onSelect }, ref) {
  const { theme } = useTheme();
  const palette = getCatalogPalette(theme);
  const [visibilityRef, visible] = useOnScreen({ rootMargin: '300px' });
  const experience = useDiscoveryExperience(enabled && visible);
  const feed = useDiscoveryFeed({ enabled: enabled && visible && experience.ready, history: experience.history });
  const catalog = useDiscoveryCatalog({ enabled: enabled && visible && feed.items.length >= 6, contextKey: feed.key });
  const [contentWidth, setContentWidth] = useState(0);
  useImperativeHandle(ref, () => ({ loadMore: () => { if (!feed.error) feed.loadMore(); } }), [feed.loadMore, feed.error]);
  const columns = contentWidth >= 850 ? 3 : contentWidth >= 510 ? 2 : 1;
  const cardWidth = contentWidth ? (contentWidth - (columns - 1) * 14) / columns : '100%';
  const button = (label, onPress) => <Pressable accessibilityRole="button" accessibilityLabel={label} onPress={onPress} style={styles.action}>
    <Text size="xs" color="secondary">{label}</Text>
  </Pressable>;
  if (!enabled) return null;
  return <View ref={visibilityRef} style={[styles.root, { borderTopColor: palette.border }]} testID="home-discovery-feed">
    <View style={styles.header}>
      <Text accessibilityRole="header" size="xl" weight="semibold">For you</Text>
    </View>
    <RecentDiscovery mode="chat" palette={palette} onUse={experience.record} />
    <LocalBriefing enabled={enabled && visible} contextKey={feed.key} />
    <View onLayout={event => setContentWidth(event.nativeEvent.layout.width)} style={styles.grid}>{Array.from({ length: Math.ceil(feed.items.length / 6) }, (_, group) => {
      const items = feed.items.slice(group * 6, group * 6 + 6);
      const featureKind = ['tasks', 'tools', 'maps'][group % 3];
      return <React.Fragment key={group}>
        <IdeaGroup items={items} groupIndex={group} width={contentWidth} palette={palette} onSeen={experience.record} onSelect={item => { experience.record(item, 'used'); onSelect(item.prompt); }} />
        {items.length === 6 && <DiscoveryFeatureSection kind={featureKind} items={catalog[featureKind]} page={Math.floor(group / 3)}
          palette={palette} columns={columns} cardWidth={cardWidth} />}
      </React.Fragment>;
    })}</View>
    {feed.loading && <View style={styles.footer} accessibilityLiveRegion="polite"><ActivityIndicator color={palette.icon} /><Text size="s" color="secondary">Finding more ideas…</Text></View>}
    {!!feed.error && <View style={styles.footer}><Text size="s" color="secondary" accessibilityLiveRegion="polite">{feed.error}</Text>{!feed.retryBlocked && button('Try again', feed.loadMore)}</View>}
    {feed.loaded && !feed.loading && !feed.error && !feed.cursor && <Text style={styles.footer} size="s" color="secondary">{feed.items.length ? 'You’re caught up. Come back later for fresh ideas.' : 'No ideas to show right now. Please try again later.'}</Text>}
    {feed.loaded && !feed.loading && !feed.error && !!feed.cursor && button('More ideas', feed.loadMore)}
  </View>;
});
export default HomeDiscoveryFeed;
const styles = StyleSheet.create({
  root: { width: '100%', maxWidth: 1080, alignSelf: 'center', borderTopWidth: StyleSheet.hairlineWidth, paddingTop: 24, paddingHorizontal: 20, paddingBottom: 36 },
  header: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 20 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', gap: 14 },
  action: { minHeight: 36, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 9, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7 },
  footer: { paddingVertical: 28, alignItems: 'center', textAlign: 'center', gap: 10 },
});
